/* eslint-disable @applitools/compat/no-async-await */
import { boot, createEraroError, eraroCodesMap } from '@dazn/bootstrap-lib';

import { SafeModeSource, SafeModeConfig } from '../configuration';
import { BRANDS } from '../configuration/constants';
import { SAFEMODE_STAUS } from '../constants/safemode.constants';
import { SetCurrentChapter } from '../public-api/lib/SetCurrentChapter';

import { captureException } from './captureException';
import { fetchSafeMode } from './fetchSafeMode';
import { handleError, HandleError } from './handleError';
import {
    parseSafeModeStatusGCP,
    parseSafeModeStatusGCPFireDrill,
    SafeModeResponse,
} from './parseSafeModeStatus';

type VariableAnnonymousInteger = (key: string, varKey: string) => number | null;

export interface ErrorHandlingInput {
    config: {
        /**
         * Name of chapter which should be loaded if access is not allowed to user because of restricted country
         */
        restrictedCountryChapterName: boot.config.ChapterName;
        /**
         * Name of chapter which should be loaded in case of error
         */
        errorChapterName: boot.config.ChapterName;
        authChapterName: boot.config.ChapterName;
    };
    metaData: {
        setCurrentChapter: SetCurrentChapter;
        /**
         * Function to trigger error
         */
        sendErrorEvent: HandleError['sendErrorEvent'];
    };
    getSafeModeConfig: () => SafeModeConfig;
    /**
     * Fetch function (default window.fetch)
     */
    fetch: boot.util.Fetch;
    getVariableIntegerAnonymous: VariableAnnonymousInteger;
    getBrandName: () => boot.brand.BrandName;
}

export interface ErrorHandlingOutput {
    /**
     * Makes remote call to log error info (used to be sentry handler, replaced with new-relic)
     */
    captureException: typeof captureException;
    /**
     * A factory function that returns a custom error object which can be handled by `handleError` method. All errors thrown from modules should be generated using this method
     */
    generateError: typeof createEraroError;
    /**
     * List of error codes to be used by other modules to generate errors
     */
    errorMap: typeof eraroCodesMap;
    /**
     * Function to fetch the actual safe mode data from lite.dazn.com (fireball)
     */
    fetchSafeMode: () => Promise<SafeModeResponse>;
    /**
     * Error handler able to process the error objects created by using `generateError` function
     */
    handleError: (
        defaultCategory: string | undefined,
        defaultCode: string | undefined,
        defaultInternal: string | undefined,
        chapterName: string,
        error: Error,
    ) => void;
}
const safeModeParserBySourceMap = {
    [SafeModeSource.GCPFireDrill]: parseSafeModeStatusGCPFireDrill,
    [SafeModeSource.GCP]: parseSafeModeStatusGCP,
};

export interface fetchSafeModeAsyncInput {
    getSafeModeConfig: () => SafeModeConfig;
    fetch: boot.util.Fetch;
    getBrandName: () => boot.brand.BrandName;
}

export const fetchSafeModeAsync = async ({
    getSafeModeConfig,
    fetch,
    getBrandName,
}: fetchSafeModeAsyncInput): Promise<SafeModeResponse> => {
    const brandName = getBrandName();
    if (
        brandName === BRANDS.BINGE ||
        brandName === BRANDS.FOXTELGNC ||
        brandName === BRANDS.FOXSPORTS
    ) {
        return {
            countryCode: '',
            status: '',
        };
    }
    try {
        const { statusURL, source } = getSafeModeConfig();

        const parseResponse =
            safeModeParserBySourceMap[source] ||
            safeModeParserBySourceMap[SafeModeSource.GCP];

        const safeModeData = await fetchSafeMode(
            fetch,
            statusURL,
            parseResponse,
        );

        return {
            countryCode: safeModeData?.countryCode,
            status:
                safeModeData?.status === SAFEMODE_STAUS.RED
                    ? SAFEMODE_STAUS.RED
                    : SAFEMODE_STAUS.GREEN,
        };
    } catch (err) {
        console.warn(err);
        return {
            countryCode: '',
            status: '',
        };
    }
};
/**
 * Module that handles logic around error handling, throwing and monitoring.
 */

export const createErrorHandlingModule = ({
    config: { restrictedCountryChapterName, errorChapterName, authChapterName },
    metaData: { setCurrentChapter, sendErrorEvent },
    getSafeModeConfig,
    fetch,
    getBrandName,
}: ErrorHandlingInput): ErrorHandlingOutput => ({
    captureException,
    generateError: createEraroError,
    errorMap: eraroCodesMap,
    fetchSafeMode: () =>
        fetchSafeModeAsync({ getSafeModeConfig, fetch, getBrandName }),
    handleError: (
        // eslint-disable-next-line @typescript-eslint/default-param-last
        defaultCategory = eraroCodesMap.category.General,
        // eslint-disable-next-line @typescript-eslint/default-param-last
        defaultCode = eraroCodesMap.code.GenericError,
        // eslint-disable-next-line @typescript-eslint/default-param-last
        defaultInternal = eraroCodesMap.internal.GenericError,
        chapterName,
        error,
    ) =>
        handleError({
            loadChapter: setCurrentChapter,
            restrictedCountryChapterName,
            errorChapterName,
            currentChapterName: chapterName,
            error,
            sendErrorEvent,
            authChapterName,
            defaultCategory,
            defaultCode,
            defaultInternal,
            fetchSafeMode: () =>
                fetchSafeModeAsync({ getSafeModeConfig, fetch, getBrandName }),
            getSafeModeConfig,
            getBrandName,
        }),
});
